(function (global) {
  "use strict";

  var STORAGE_KEY = "bd_objection_inventory_v1";
  var MAX_ENTRIES = 20;
  var MAX_CAPTURED_SIGNATURES = 20;
  var CAPTURE_ATTEMPT_TTL_MS = 2 * 60 * 1000;
  var MIN_ENTRIES = 3;
  var SHIPPING_BAND_PERCENT = 15;
  var PERCENT_COMPARISON_TOLERANCE = 1e-9;
  var STAGE_KEYS = ["worries", "entries", "shipping"];
  var WORRY_KEYS = ["fit", "arrival", "suitability", "returns", "trust"];
  var CATEGORY_KEYS = ["fit", "delivery", "returns", "trust", "price", "shipping", "other"];
  var CATEGORY_LABELS = {
    fit: "fit or suitability",
    delivery: "delivery time",
    returns: "returns",
    trust: "trust",
    price: "price",
    shipping: "shipping",
    other: "something else"
  };
  var SOURCE_LABELS = {
    pre_purchase: "pre-purchase question",
    post_purchase: "post-purchase complaint",
    abandoned: "conversation that went quiet"
  };
  var MEMORYLESS_STORAGE = {
    getItem: function () { return null; },
    setItem: function () {}
  };

  function queryAll(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function text(value) {
    return String(value == null ? "" : value).trim();
  }

  function safeSessionStorage(scope) {
    try {
      return scope.sessionStorage || MEMORYLESS_STORAGE;
    } catch (_error) {
      return MEMORYLESS_STORAGE;
    }
  }

  function bindStateToCustomer(state, currentCustomerId) {
    var current = text(currentCustomerId).slice(0, 64);
    var owner = text(state && state.ownerCustomerId).slice(0, 64);
    var bound = owner && owner !== current ? defaultState() : state;
    bound.ownerCustomerId = current;
    return bound;
  }

  function createCaptureLedger(initialSignatures, initialAttempts, nowProvider) {
    var completed = Object.create(null);
    var completedOrder = [];
    var attempts = Object.create(null);
    var attemptOrder = [];
    var desiredSignature = "";
    var now = typeof nowProvider === "function" ? nowProvider : function () { return Date.now(); };

    function rememberCompleted(signature) {
      if (!signature || completed[signature]) return;
      completed[signature] = true;
      completedOrder.push(signature);
      if (completedOrder.length > MAX_CAPTURED_SIGNATURES) {
        delete completed[completedOrder.shift()];
      }
    }

    function rememberAttempt(signature, startedAt) {
      if (
        !signature ||
        completed[signature] ||
        Object.prototype.hasOwnProperty.call(attempts, signature)
      ) return;
      var current = now();
      var restored = Number(startedAt);
      attempts[signature] = isFinite(restored) && restored >= 0
        ? Math.min(restored, current)
        : current;
      attemptOrder.push(signature);
      if (attemptOrder.length > MAX_CAPTURED_SIGNATURES) {
        delete attempts[attemptOrder.shift()];
      }
    }

    function forgetAttempt(signature) {
      if (!Object.prototype.hasOwnProperty.call(attempts, signature)) return;
      delete attempts[signature];
      attemptOrder = attemptOrder.filter(function (attempt) { return attempt !== signature; });
    }

    function pruneAttempts() {
      var current = now();
      attemptOrder.slice().forEach(function (signature) {
        if (current - attempts[signature] >= CAPTURE_ATTEMPT_TTL_MS) {
          forgetAttempt(signature);
        }
      });
    }

    (Array.isArray(initialSignatures) ? initialSignatures : []).forEach(function (signature) {
      rememberCompleted(text(signature));
    });
    (Array.isArray(initialAttempts) ? initialAttempts : []).forEach(function (attempt) {
      var restored = attempt && typeof attempt === "object" ? attempt : null;
      rememberAttempt(
        text(restored ? restored.signature : attempt),
        restored ? restored.startedAt : now()
      );
    });

    return {
      begin: function (signature) {
        desiredSignature = signature;
        pruneAttempts();
        if (completed[signature] || Object.prototype.hasOwnProperty.call(attempts, signature)) return false;
        rememberAttempt(signature, now());
        return true;
      },
      settle: function (signature, succeeded) {
        forgetAttempt(signature);
        if (succeeded === true) rememberCompleted(signature);
        return succeeded === true && desiredSignature === signature;
      },
      completedSignatures: function () {
        return completedOrder.slice();
      },
      captureAttempts: function () {
        pruneAttempts();
        return attemptOrder.map(function (signature) {
          return { signature: signature, startedAt: attempts[signature] };
        });
      }
    };
  }

  function number(value) {
    if (value === "" || value == null) return null;
    var parsed = Number(value);
    return isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  function comparatorEvidence(item) {
    var name = text(item && item.name);
    var rawPrice = text(item && item.price);
    var price = number(item && item.price);
    return {
      empty: !name && !rawPrice,
      complete: Boolean(name && rawPrice && price !== null),
      price: price
    };
  }

  function round(value, places) {
    var factor = Math.pow(10, places || 0);
    return Math.round(value * factor) / factor;
  }

  function median(values) {
    var ordered = values.slice().sort(function (a, b) { return a - b; });
    if (!ordered.length) return null;
    var middle = Math.floor(ordered.length / 2);
    return ordered.length % 2
      ? ordered[middle]
      : (ordered[middle - 1] + ordered[middle]) / 2;
  }

  function categoryLabel(key) {
    return CATEGORY_LABELS[key] || CATEGORY_LABELS.other;
  }

  function sourceLabel(key) {
    return SOURCE_LABELS[key] || "customer message";
  }

  function completeEntries(entries) {
    return (entries || []).filter(function (entry) {
      return text(entry && entry.text) &&
        CATEGORY_KEYS.indexOf(entry.category) > -1 &&
        Object.prototype.hasOwnProperty.call(SOURCE_LABELS, entry.source);
    });
  }

  function computeFriction(worries) {
    var score = 0;
    var answered = 0;
    WORRY_KEYS.forEach(function (key) {
      var value = worries && worries[key];
      if (value === "clear") {
        score += 1;
        answered += 1;
      } else if (value === "buried") {
        score += 0.5;
        answered += 1;
      } else if (value === "missing") {
        answered += 1;
      }
    });
    return { score: round(score, 1), answered: answered, total: WORRY_KEYS.length };
  }

  function buildCategoryProfile(entries) {
    var complete = completeEntries(entries);
    var counts = {};
    CATEGORY_KEYS.forEach(function (key) { counts[key] = 0; });
    complete.forEach(function (entry) { counts[entry.category] += 1; });

    var ranked = CATEGORY_KEYS.map(function (key, index) {
      return { key: key, label: categoryLabel(key), count: counts[key], order: index };
    }).filter(function (item) {
      return item.count > 0;
    }).sort(function (a, b) {
      return b.count - a.count || a.order - b.order;
    });

    var tied = ranked.length > 1 && ranked[0].count === ranked[1].count;
    var enoughEvidence = complete.length >= MIN_ENTRIES;

    return {
      total: complete.length,
      counts: counts,
      ranked: ranked,
      top: enoughEvidence && ranked.length && !tied ? ranked[0] : null,
      tied: tied
    };
  }

  function computeShipping(shipping) {
    shipping = shipping || {};
    if (shipping.free === true) {
      return {
        key: "hidden",
        label: "No separate shipping charge",
        median: null,
        difference: null,
        position: null,
        comparators: 0
      };
    }

    var own = number(shipping.standard);
    var comparatorPrices = (shipping.comparators || []).map(comparatorEvidence).filter(function (evidence) {
      return evidence.complete;
    }).map(function (evidence) {
      return evidence.price;
    });

    if (own === null || comparatorPrices.length < 2) {
      return {
        key: "unknown",
        label: "Not enough prices to compare",
        median: comparatorPrices.length ? round(median(comparatorPrices), 2) : null,
        difference: null,
        position: null,
        comparators: comparatorPrices.length
      };
    }

    var marketMedian = median(comparatorPrices);
    var difference = marketMedian === 0
      ? null
      : ((own - marketMedian) / marketMedian) * 100;
    var above = marketMedian === 0
      ? own > 0
      : difference > SHIPPING_BAND_PERCENT + PERCENT_COMPARISON_TOLERANCE;
    var below = difference !== null &&
      difference < -SHIPPING_BAND_PERCENT - PERCENT_COMPARISON_TOLERANCE;

    return {
      key: above ? "above" : "market",
      label: above ? "Above the middle price" : "At or below the middle price",
      median: round(marketMedian, 2),
      difference: difference === null ? null : round(difference, 1),
      position: above ? "above" : (below ? "below" : "within"),
      comparators: comparatorPrices.length
    };
  }

  function computeResult(state) {
    var friction = computeFriction(state && state.worries);
    var profile = buildCategoryProfile(state && state.entries);
    var shipping = computeShipping(state && state.shipping);
    return {
      friction: friction,
      profile: profile,
      shipping: shipping,
      entryCount: profile.total,
      topCategory: profile.top ? profile.top.key : "mixed"
    };
  }

  function validate(state) {
    var errors = [];
    var friction = computeFriction(state && state.worries);
    var entries = (state && state.entries) || [];
    var complete = completeEntries(entries);
    var partialEntry = entries.some(function (entry) {
      var hasAnything = text(entry && entry.text) || text(entry && entry.category) || text(entry && entry.source);
      var isComplete = text(entry && entry.text) &&
        CATEGORY_KEYS.indexOf(entry.category) > -1 &&
        Object.prototype.hasOwnProperty.call(SOURCE_LABELS, entry.source);
      return Boolean(hasAnything && !isComplete);
    });

    if (friction.answered < WORRY_KEYS.length) {
      errors.push({ step: "worries", message: "Answer all five page checks." });
    }
    if (partialEntry) {
      errors.push({ step: "entries", message: "Finish the customer line you started, or remove it." });
    }

    var shipping = (state && state.shipping) || {};
    var shippingContextFields = [];
    if (!text(shipping.currency)) shippingContextFields.push("currency");
    if (!text(shipping.itemType)) shippingContextFields.push("itemType");
    if (shippingContextFields.length) {
      errors.push({
        step: "shipping",
        fields: shippingContextFields,
        message: "Choose a currency and the closest item type for the shipping check."
      });
    }
    if (shipping.free !== true && (number(shipping.standard) === null || number(shipping.standard) === 0)) {
      errors.push({
        step: "shipping",
        fields: ["standard"],
        message: "Add your standard shipping price, or tick that customers see free shipping."
      });
    }

    var invalidComparatorFields = [];
    (shipping.comparators || []).forEach(function (item, index) {
      var evidence = comparatorEvidence(item);
      if (evidence.empty || evidence.complete) return;
      if (!text(item && item.name)) invalidComparatorFields.push("comparator:" + index + ":name");
      if (!text(item && item.price) || number(item && item.price) === null) {
        invalidComparatorFields.push("comparator:" + index + ":price");
      }
    });
    if (invalidComparatorFields.length) {
      errors.push({
        step: "shipping",
        fields: invalidComparatorFields,
        message: "For each comparison store you add, include both the store name and its standard shipping price. Use the currency you chose above."
      });
    }
    return errors;
  }

  function formatMoney(value, currency) {
    if (value === null || value === undefined) return "";
    try {
      return new Intl.NumberFormat("en-NZ", {
        style: "currency",
        currency: currency || "NZD",
        maximumFractionDigits: Number(value) % 1 ? 2 : 0
      }).format(value);
    } catch (_error) {
      return (currency || "") + " " + value;
    }
  }

  function shippingDetail(shipping, currency) {
    if (shipping.key === "hidden") {
      return "Your customer sees free shipping. Check that the cost still works in your margin, then keep the checkout line simple.";
    }
    if (shipping.key === "unknown") {
      return "Add the standard shipping price from at least two bigger stores selling a similar item. Use the same currency for all of them.";
    }
    if (shipping.key === "above") {
      if (shipping.difference === null) {
        return "The stores you chose offer free standard shipping, while you charge for it. Your customers can price shipping even when they can’t price your product.";
      }
      return "Your standard rate is " + Math.max(0, shipping.difference) + "% above the middle of the prices you entered, which is " +
        formatMoney(shipping.median, currency) + ". Your customers can price shipping even when they can’t price your product.";
    }
    if (shipping.position === "below") {
      return "Your standard rate is " + Math.abs(shipping.difference) + "% below the middle of the prices you entered, which is " +
        formatMoney(shipping.median, currency) + ". This check doesn’t point to shipping as the main problem.";
    }
    return "Your standard rate sits within " + SHIPPING_BAND_PERCENT + "% of the middle of the prices you entered, which is " +
      formatMoney(shipping.median, currency) + ". This check doesn’t point to shipping as the main problem.";
  }

  function emptyEntry(id) {
    return { id: id, text: "", category: "", source: "" };
  }

  function defaultState() {
    return {
      version: 1,
      activeStage: "worries",
      ownerCustomerId: "",
      worries: {},
      entries: [emptyEntry(1), emptyEntry(2), emptyEntry(3)],
      shipping: {
        currency: "NZD",
        itemType: "",
        standard: "",
        rural: "",
        free: false,
        comparators: [
          { name: "", price: "" },
          { name: "", price: "" },
          { name: "", price: "" }
        ]
      },
      submitted: false,
      capturedSignatures: [],
      captureAttempts: []
    };
  }

  function normaliseState(raw) {
    var base = defaultState();
    if (!raw || raw.version !== 1) return base;
    base.activeStage = STAGE_KEYS.indexOf(raw.activeStage) > -1 ? raw.activeStage : "worries";
    base.ownerCustomerId = text(raw.ownerCustomerId).slice(0, 64);
    base.worries = raw.worries && typeof raw.worries === "object" ? raw.worries : {};
    base.entries = Array.isArray(raw.entries)
      ? raw.entries.slice(0, MAX_ENTRIES).map(function (entry, index) {
        return {
          id: Number(entry.id) || index + 1,
          text: String(entry.text || "").slice(0, 280),
          category: CATEGORY_KEYS.indexOf(entry.category) > -1 ? entry.category : "",
          source: Object.prototype.hasOwnProperty.call(SOURCE_LABELS, entry.source) ? entry.source : ""
        };
      })
      : base.entries;
    while (base.entries.length < MIN_ENTRIES) {
      base.entries.push(emptyEntry(base.entries.length + 1));
    }
    var incomingShipping = raw.shipping || {};
    base.shipping = {
      currency: text(incomingShipping.currency) || "NZD",
      itemType: text(incomingShipping.itemType),
      standard: incomingShipping.standard == null ? "" : String(incomingShipping.standard),
      rural: incomingShipping.rural == null ? "" : String(incomingShipping.rural),
      free: incomingShipping.free === true,
      comparators: [0, 1, 2].map(function (index) {
        var item = (incomingShipping.comparators || [])[index] || {};
        return { name: String(item.name || "").slice(0, 80), price: item.price == null ? "" : String(item.price) };
      })
    };
    base.submitted = raw.submitted === true;
    var capturedSignatures = Array.isArray(raw.capturedSignatures)
      ? raw.capturedSignatures.slice()
      : [];
    if (text(raw.capturedSignature)) capturedSignatures.push(text(raw.capturedSignature));
    var captureLedger = createCaptureLedger(capturedSignatures, raw.captureAttempts);
    base.capturedSignatures = captureLedger.completedSignatures();
    base.captureAttempts = captureLedger.captureAttempts();
    return base;
  }

  function loadState(storage) {
    try {
      return normaliseState(JSON.parse(storage.getItem(STORAGE_KEY) || "null"));
    } catch (_error) {
      return defaultState();
    }
  }

  function storeState(storage, state) {
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_error) {
      // The worksheet still works when storage is blocked.
    }
  }

  function createOption(value, label) {
    var option = global.document.createElement("option");
    option.value = value;
    option.textContent = label;
    return option;
  }

  function init(root) {
    if (root.dataset.bdObjectionInventoryReady === "true") return;
    root.dataset.bdObjectionInventoryReady = "true";

    var storage = safeSessionStorage(global);
    var currentCustomerId = text(root.dataset.customerId);
    var state = bindStateToCustomer(loadState(storage), currentCustomerId);
    storeState(storage, state);
    var isLoggedIn = Boolean(currentCustomerId) && root.dataset.loggedIn === "true";
    var captureEndpoint = root.dataset.captureEndpoint || "";
    var captureLedger = createCaptureLedger(state.capturedSignatures, state.captureAttempts);
    var entryList = root.querySelector("[data-bd-entry-list]");
    var addEntryButton = root.querySelector("[data-bd-add-entry]");
    var form = root.querySelector("[data-bd-form]");
    var resultPanel = root.querySelector("[data-bd-result]");
    var stagePanels = queryAll(root, "[data-bd-stage-panel]");
    var fixList = root.querySelector("[data-bd-fix-list]");
    var fixMap = null;
    var fixMapNode = root.querySelector("[data-bd-fix-map]");
    var nextId = state.entries.reduce(function (highest, entry) {
      return Math.max(highest, Number(entry.id) || 0);
    }, 0) + 1;

    if (fixMapNode) {
      try { fixMap = JSON.parse(fixMapNode.textContent); } catch (_error) { fixMap = null; }
    }

    function persist() {
      storeState(storage, state);
    }

    function persistCaptureLedger() {
      state.capturedSignatures = captureLedger.completedSignatures();
      state.captureAttempts = captureLedger.captureAttempts();
      persist();
    }

    function clearStageInvalidState(stage) {
      queryAll(root, '[aria-invalid="true"]').forEach(function (node) {
        if (!stage || node.closest('[data-bd-stage-panel="' + stage + '"]')) {
          node.removeAttribute("aria-invalid");
          node.removeAttribute("aria-describedby");
        }
      });
    }

    function clearStageErrors(stage) {
      queryAll(root, "[data-bd-stage-errors]").forEach(function (summary) {
        if (stage && summary.dataset.bdStageErrors !== stage) return;
        summary.hidden = true;
        summary.innerHTML = "";
        delete summary.dataset.bdErrorSignature;
      });
      clearStageInvalidState(stage);
    }

    function setActiveStage(stage, options) {
      if (STAGE_KEYS.indexOf(stage) < 0) stage = "worries";
      state.activeStage = stage;
      stagePanels.forEach(function (panel) {
        panel.hidden = panel.dataset.bdStagePanel !== stage;
      });
      updateProgress();
      persist();

      options = options || {};
      var activePanel = root.querySelector('[data-bd-stage-panel="' + stage + '"]');
      if (activePanel && options.scroll) {
        activePanel.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      if (activePanel && options.focus) {
        var heading = activePanel.querySelector("h2");
        if (heading) {
          heading.setAttribute("tabindex", "-1");
          try { heading.focus({ preventScroll: true }); } catch (_error) { heading.focus(); }
        }
      }
    }

    function stageValidation(stage) {
      return validate(state).filter(function (error) { return error.step === stage; });
    }

    function shippingField(fieldKey) {
      if (fieldKey.indexOf("comparator:") !== 0) {
        return root.querySelector('[data-bd-shipping="' + fieldKey + '"]');
      }
      var parts = fieldKey.split(":");
      var row = queryAll(root, "[data-bd-comparator]")[Number(parts[1])];
      return row ? row.querySelector('[data-bd-comparator-field="' + parts[2] + '"]') : null;
    }

    function renderStageErrors(stage, errors, options) {
      var summary = root.querySelector('[data-bd-stage-errors="' + stage + '"]');
      clearStageInvalidState(stage);
      if (!summary) return;
      if (!errors.length) {
        clearStageErrors(stage);
        return;
      }

      var signature = errors.map(function (error) { return error.message; }).join("\n");
      if (summary.hidden || summary.dataset.bdErrorSignature !== signature) {
        summary.innerHTML = "";
        var title = global.document.createElement("strong");
        title.textContent = "Finish this before you continue:";
        summary.appendChild(title);
        var list = global.document.createElement("ul");
        errors.forEach(function (error) {
          var item = global.document.createElement("li");
          item.textContent = error.message;
          list.appendChild(item);
        });
        summary.appendChild(list);
        summary.dataset.bdErrorSignature = signature;
      }
      summary.hidden = false;

      if (stage === "worries") {
        queryAll(root, "[data-bd-worry-group]").forEach(function (group) {
          if (!group.querySelector("input:checked")) {
            group.setAttribute("aria-invalid", "true");
            group.setAttribute("aria-describedby", summary.id);
          }
        });
      }
      if (stage === "entries") {
        queryAll(entryList, ".entry-card").forEach(function (row, index) {
          var entry = state.entries[index] || {};
          var hasAnything = text(entry.text) || text(entry.category) || text(entry.source);
          var complete = text(entry.text) && CATEGORY_KEYS.indexOf(entry.category) > -1 &&
            Object.prototype.hasOwnProperty.call(SOURCE_LABELS, entry.source);
          if (hasAnything && !complete) {
            row.setAttribute("aria-invalid", "true");
            row.setAttribute("aria-describedby", summary.id);
          }
        });
      }
      if (stage === "shipping") {
        errors.forEach(function (error) {
          (error.fields || []).forEach(function (fieldKey) {
            var field = shippingField(fieldKey);
            if (!field) return;
            field.setAttribute("aria-invalid", "true");
            field.setAttribute("aria-describedby", summary.id);
          });
        });
      }

      options = options || {};
      if (options.focus !== false) {
        summary.scrollIntoView({ behavior: "smooth", block: "center" });
        try { summary.focus({ preventScroll: true }); } catch (_error) { summary.focus(); }
      }
    }

    function markChanged() {
      state.submitted = false;
      if (resultPanel) resultPanel.hidden = true;
      var summary = root.querySelector('[data-bd-stage-errors="' + state.activeStage + '"]');
      if (summary && !summary.hidden) {
        var currentErrors = stageValidation(state.activeStage);
        renderStageErrors(state.activeStage, currentErrors, { focus: false });
      } else {
        clearStageErrors(state.activeStage);
      }
      persist();
      updateProgress();
    }

    function buildEntryRow(entry, index) {
      var row = global.document.createElement("article");
      row.className = "entry-card";
      row.dataset.entryId = entry.id;

      var head = global.document.createElement("div");
      head.className = "entry-head";
      var title = global.document.createElement("h4");
      title.textContent = "Customer line " + (index + 1);
      head.appendChild(title);

      var remove = global.document.createElement("button");
      remove.className = "entry-remove";
      remove.type = "button";
      remove.textContent = "Remove";
      remove.setAttribute("aria-label", "Remove customer line " + (index + 1));
      remove.hidden = state.entries.length <= MIN_ENTRIES;
      remove.addEventListener("click", function () {
        state.entries = state.entries.filter(function (item) { return item.id !== entry.id; });
        renderEntries();
        markChanged();
      });
      head.appendChild(remove);
      row.appendChild(head);

      var textareaId = root.id + "-entry-" + entry.id;
      var label = global.document.createElement("label");
      label.className = "field-label";
      label.htmlFor = textareaId;
      label.textContent = "Paste the customer’s exact words";
      row.appendChild(label);

      var textarea = global.document.createElement("textarea");
      textarea.id = textareaId;
      textarea.maxLength = 280;
      textarea.rows = 3;
      textarea.placeholder = "Will this arrive before Friday?";
      textarea.value = entry.text;
      textarea.addEventListener("input", function () {
        entry.text = textarea.value.slice(0, 280);
        count.textContent = entry.text.length + "/280";
        markChanged();
      });
      row.appendChild(textarea);

      var count = global.document.createElement("span");
      count.className = "char-count";
      count.setAttribute("aria-live", "polite");
      count.textContent = entry.text.length + "/280";
      row.appendChild(count);

      var pair = global.document.createElement("div");
      pair.className = "field-pair";

      var categoryWrap = global.document.createElement("div");
      var categoryLabelNode = global.document.createElement("label");
      var categoryId = textareaId + "-category";
      categoryLabelNode.className = "field-label";
      categoryLabelNode.htmlFor = categoryId;
      categoryLabelNode.textContent = "What were they worried about?";
      categoryWrap.appendChild(categoryLabelNode);
      var category = global.document.createElement("select");
      category.id = categoryId;
      category.appendChild(createOption("", "Choose the closest one"));
      CATEGORY_KEYS.forEach(function (key) { category.appendChild(createOption(key, categoryLabel(key))); });
      category.value = entry.category;
      category.addEventListener("change", function () { entry.category = category.value; markChanged(); });
      categoryWrap.appendChild(category);
      pair.appendChild(categoryWrap);

      var sourceWrap = global.document.createElement("div");
      var sourceLabelNode = global.document.createElement("label");
      var sourceId = textareaId + "-source";
      sourceLabelNode.className = "field-label";
      sourceLabelNode.htmlFor = sourceId;
      sourceLabelNode.textContent = "Where did the message come from?";
      sourceWrap.appendChild(sourceLabelNode);
      var source = global.document.createElement("select");
      source.id = sourceId;
      source.appendChild(createOption("", "Choose where it came from"));
      Object.keys(SOURCE_LABELS).forEach(function (key) { source.appendChild(createOption(key, sourceLabel(key))); });
      source.value = entry.source;
      source.addEventListener("change", function () { entry.source = source.value; markChanged(); });
      sourceWrap.appendChild(source);
      pair.appendChild(sourceWrap);
      row.appendChild(pair);
      return row;
    }

    function renderEntries() {
      entryList.innerHTML = "";
      state.entries.forEach(function (entry, index) {
        entryList.appendChild(buildEntryRow(entry, index));
      });
      addEntryButton.disabled = state.entries.length >= MAX_ENTRIES;
      addEntryButton.textContent = state.entries.length >= MAX_ENTRIES
        ? "20 customer lines added"
        : "+ Add another customer line";
      var countNode = root.querySelector("[data-bd-entry-count]");
      if (countNode) {
        var ready = completeEntries(state.entries).length;
        countNode.textContent = ready + (ready === 1 ? " line ready" : " lines ready");
      }
    }

    function readShipping() {
      state.shipping.currency = root.querySelector('[data-bd-shipping="currency"]').value;
      state.shipping.itemType = root.querySelector('[data-bd-shipping="itemType"]').value;
      state.shipping.standard = root.querySelector('[data-bd-shipping="standard"]').value;
      state.shipping.rural = root.querySelector('[data-bd-shipping="rural"]').value;
      state.shipping.free = root.querySelector('[data-bd-shipping="free"]').checked;
      state.shipping.comparators = queryAll(root, "[data-bd-comparator]").map(function (row) {
        return {
          name: row.querySelector('[data-bd-comparator-field="name"]').value,
          price: row.querySelector('[data-bd-comparator-field="price"]').value
        };
      });
    }

    function writeStateToForm() {
      Object.keys(state.worries).forEach(function (key) {
        var selector = '[name="' + root.id + "-worry-" + key + '"][value="' + state.worries[key] + '"]';
        var radio = root.querySelector(selector);
        if (radio) radio.checked = true;
      });
      root.querySelector('[data-bd-shipping="currency"]').value = state.shipping.currency;
      root.querySelector('[data-bd-shipping="itemType"]').value = state.shipping.itemType;
      root.querySelector('[data-bd-shipping="standard"]').value = state.shipping.standard;
      root.querySelector('[data-bd-shipping="rural"]').value = state.shipping.rural;
      root.querySelector('[data-bd-shipping="free"]').checked = state.shipping.free;
      queryAll(root, "[data-bd-comparator]").forEach(function (row, index) {
        var comparator = state.shipping.comparators[index] || { name: "", price: "" };
        row.querySelector('[data-bd-comparator-field="name"]').value = comparator.name;
        row.querySelector('[data-bd-comparator-field="price"]').value = comparator.price;
      });
      toggleShippingPrice();
    }

    function toggleShippingPrice() {
      var isFree = root.querySelector('[data-bd-shipping="free"]').checked;
      queryAll(root, "[data-bd-own-shipping]").forEach(function (node) {
        node.hidden = isFree;
        queryAll(node, "input").forEach(function (input) { input.disabled = isFree; });
      });
    }

    function updateProgress() {
      var friction = computeFriction(state.worries);
      var entriesDone = completeEntries(state.entries).length >= MIN_ENTRIES;
      var shippingDone = Boolean(state.shipping.itemType) &&
        Boolean(state.shipping.currency) &&
        (state.shipping.free || number(state.shipping.standard) > 0);
      var status = {
        worries: friction.answered === WORRY_KEYS.length,
        entries: entriesDone,
        shipping: shippingDone
      };
      queryAll(root, "[data-bd-progress-step]").forEach(function (node) {
        var key = node.dataset.bdProgressStep;
        node.classList.toggle("is-done", Boolean(status[key]));
        node.classList.toggle("is-active", key === state.activeStage);
        if (key === state.activeStage) node.setAttribute("aria-current", "step");
        else node.removeAttribute("aria-current");
      });
      var countNode = root.querySelector("[data-bd-entry-count]");
      if (countNode) {
        var ready = completeEntries(state.entries).length;
        countNode.textContent = ready + (ready === 1 ? " line ready" : " lines ready");
      }
    }

    function renderCategoryProfile(result) {
      var list = root.querySelector("[data-bd-category-list]");
      list.innerHTML = "";
      result.profile.ranked.forEach(function (item) {
        var row = global.document.createElement("li");
        var label = global.document.createElement("span");
        label.textContent = item.label;
        var count = global.document.createElement("b");
        count.textContent = String(item.count);
        row.appendChild(label);
        row.appendChild(count);
        list.appendChild(row);
      });
    }

    function renderFixList(result) {
      if (!fixList || !fixMap) return;
      fixList.innerHTML = "";
      result.profile.ranked.forEach(function (group) {
        var section = global.document.createElement("section");
        section.className = "fix-group";
        var head = global.document.createElement("div");
        head.className = "fix-group-head";
        var title = global.document.createElement("h4");
        title.textContent = categoryLabel(group.key);
        var badge = global.document.createElement("span");
        badge.textContent = group.count + (group.count === 1 ? " line" : " lines");
        head.appendChild(title);
        head.appendChild(badge);
        section.appendChild(head);

        var placement = global.document.createElement("p");
        placement.className = "placement";
        placement.textContent = "Answer it here: " + (fixMap[group.key] || fixMap.other);
        section.appendChild(placement);

        var list = global.document.createElement("ol");
        completeEntries(state.entries).filter(function (entry) {
          return entry.category === group.key;
        }).forEach(function (entry) {
          var item = global.document.createElement("li");
          var quote = global.document.createElement("blockquote");
          quote.textContent = "“" + text(entry.text) + "”";
          var source = global.document.createElement("span");
          source.textContent = sourceLabel(entry.source);
          item.appendChild(quote);
          item.appendChild(source);
          list.appendChild(item);
        });
        section.appendChild(list);
        fixList.appendChild(section);
      });
    }

    function capture(result) {
      if (!isLoggedIn || !captureEndpoint || !global.fetch) return;
      var payload = {
        tool: "objectionInventory",
        schemaVersion: 1,
        frictionScore: result.friction.score,
        categoryProfile: result.profile.counts,
        topCategory: result.topCategory,
        shippingVerdict: result.shipping.key,
        entryCount: result.entryCount,
        worries: state.worries,
        entries: completeEntries(state.entries).map(function (entry) {
          return { text: text(entry.text), category: entry.category, source: entry.source };
        }),
        shipping: state.shipping
      };
      var email = text(root.dataset.customerEmail);
      if (email) payload.customer = { email: email };
      var signature = JSON.stringify(payload);
      if (!captureLedger.begin(signature)) return;
      persistCaptureLedger();

      global.fetch(captureEndpoint, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: signature,
        keepalive: true
      }).then(function (response) {
        if (!response || !response.ok) {
          captureLedger.settle(signature, false);
          persistCaptureLedger();
          return;
        }
        captureLedger.settle(signature, true);
        persistCaptureLedger();
      }).catch(function () {
        captureLedger.settle(signature, false);
        persistCaptureLedger();
        // Capture is retryable on the next submit and never blocks the result.
      });
    }

    function showResult(scroll) {
      var result = computeResult(state);
      var isPartial = result.entryCount < MIN_ENTRIES;
      var isMixed = !isPartial && result.profile.tied;
      var partialPanel = root.querySelector("[data-bd-partial]");
      var fullResult = root.querySelector("[data-bd-full-result]");
      root.querySelector('[data-bd-output="friction"]').textContent = result.friction.score + "/5";
      root.querySelector('[data-bd-output="friction-note"]').textContent =
        result.friction.score >= 4
          ? "Most of the five answers are easy to find. Your customer lines will show you what still slips through."
          : "Some answers are missing or taking too much hunting. That can create friction even when nobody emails you about it.";
      root.querySelector('[data-bd-output="top-category"]').textContent = isPartial
        ? "Not enough customer lines yet"
        : isMixed
          ? "No single doubt comes up most"
          : categoryLabel(result.topCategory);
      root.querySelector('[data-bd-output="top-category-note"]').textContent = isPartial
        ? "Add " + (MIN_ENTRIES - result.entryCount) + " more customer " +
          ((MIN_ENTRIES - result.entryCount) === 1 ? "line" : "lines") + ", then choose what each was about and where it came from."
        : isMixed
          ? "Two or more worries appear equally often. Add a few more real customer lines before you call one the biggest."
          : "In the lines you entered, " + categoryLabel(result.topCategory) + " comes up most.";
      root.querySelector('[data-bd-output="shipping"]').textContent = result.shipping.label;
      root.querySelector('[data-bd-output="shipping-note"]').textContent =
        shippingDetail(result.shipping, state.shipping.currency);
      renderCategoryProfile(result);
      if (partialPanel) partialPanel.hidden = !isPartial;
      if (fullResult) fullResult.hidden = isPartial;
      if (isPartial && fixList) fixList.innerHTML = "";
      if (!isPartial) renderFixList(result);
      resultPanel.hidden = false;
      clearStageErrors();
      state.submitted = true;
      persist();
      capture(result);
      if (scroll) {
        resultPanel.scrollIntoView({ behavior: "smooth", block: "start" });
        try { resultPanel.focus({ preventScroll: true }); } catch (_error) { resultPanel.focus(); }
      }
    }

    queryAll(root, "[data-bd-worry]").forEach(function (radio) {
      radio.addEventListener("change", function () {
        state.worries[radio.dataset.bdWorry] = radio.value;
        markChanged();
      });
    });

    queryAll(root, "[data-bd-shipping], [data-bd-comparator-field]").forEach(function (field) {
      field.addEventListener("input", function () {
        readShipping();
        if (field.dataset.bdShipping === "free") toggleShippingPrice();
        markChanged();
      });
      field.addEventListener("change", function () {
        readShipping();
        if (field.dataset.bdShipping === "free") toggleShippingPrice();
        markChanged();
      });
    });

    addEntryButton.addEventListener("click", function () {
      if (state.entries.length >= MAX_ENTRIES) return;
      state.entries.push(emptyEntry(nextId));
      nextId += 1;
      renderEntries();
      markChanged();
      var rows = queryAll(entryList, ".entry-card");
      var lastTextarea = rows.length ? rows[rows.length - 1].querySelector("textarea") : null;
      if (lastTextarea) lastTextarea.focus();
    });

    queryAll(root, "[data-bd-stage-continue]").forEach(function (button) {
      button.addEventListener("click", function () {
        readShipping();
        var errors = stageValidation(state.activeStage);
        if (errors.length) {
          renderStageErrors(state.activeStage, errors);
          return;
        }
        clearStageErrors(state.activeStage);
        setActiveStage(button.dataset.bdStageContinue, { scroll: true, focus: true });
      });
    });

    queryAll(root, "[data-bd-stage-back]").forEach(function (button) {
      button.addEventListener("click", function () {
        setActiveStage(button.dataset.bdStageBack, { scroll: true, focus: true });
      });
    });

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      readShipping();
      var errors = validate(state);
      if (errors.length) {
        var firstStage = errors[0].step;
        setActiveStage(firstStage, { scroll: true });
        renderStageErrors(firstStage, errors.filter(function (error) { return error.step === firstStage; }));
        return;
      }
      showResult(true);
    });

    queryAll(root, "[data-bd-register], [data-bd-login]").forEach(function (link) {
      link.addEventListener("click", persist);
    });

    var printButton = root.querySelector("[data-bd-print]");
    if (printButton) printButton.addEventListener("click", function () { global.print(); });

    var backToEntries = root.querySelector("[data-bd-back-entries]");
    if (backToEntries) backToEntries.addEventListener("click", function () {
      setActiveStage("entries", { scroll: true, focus: true });
    });

    renderEntries();
    writeStateToForm();
    setActiveStage(state.activeStage);
    if (state.submitted && !validate(state).length) showResult(false);
  }

  var API = {
    MIN_ENTRIES: MIN_ENTRIES,
    MAX_ENTRIES: MAX_ENTRIES,
    CAPTURE_ATTEMPT_TTL_MS: CAPTURE_ATTEMPT_TTL_MS,
    computeFriction: computeFriction,
    buildCategoryProfile: buildCategoryProfile,
    computeShipping: computeShipping,
    shippingDetail: shippingDetail,
    computeResult: computeResult,
    validate: validate,
    safeSessionStorage: safeSessionStorage,
    bindStateToCustomer: bindStateToCustomer,
    createCaptureLedger: createCaptureLedger,
    normaliseState: normaliseState,
    defaultState: defaultState
  };

  global.BDObjectionInventory = API;
  if (typeof module !== "undefined" && module.exports) module.exports = API;

  function boot() {
    queryAll(global.document, "[data-bd-objection-inventory]").forEach(init);
  }

  if (global.document) {
    if (global.document.readyState === "loading") {
      global.document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }
  }
})(typeof window !== "undefined" ? window : globalThis);
